/**
 * useChatExamples — few-shot SQL example list/create/delete hooks.
 *
 * Bundle of three hooks sharing the same cache key
 * (``enterpriseExamplesKey``). Mutations invalidate the query so the
 * list reconciles after every add/delete.
 *
 * Delete uses optimistic remove — same UX rationale as
 * ``useDisconnectConnection``: the list updates instantly, rollback on
 * BE error.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type CreateExampleRequest,
  type ExampleResponse,
  createEnterpriseExample,
  deleteEnterpriseExample,
  listEnterpriseExamples,
} from "@ando/core/api";

import { enterpriseExamplesKey } from "./enterpriseKeys";

const STALE_MS = 60_000;

export function useExamplesQuery(connId: string, enabled = true) {
  return useQuery<readonly ExampleResponse[]>({
    queryKey: enterpriseExamplesKey(connId),
    queryFn: () => listEnterpriseExamples(connId),
    enabled: enabled && connId.length > 0,
    staleTime: STALE_MS,
    retry: 1,
  });
}

export interface UseCreateExampleVars {
  readonly connId: string;
  readonly body: CreateExampleRequest;
}

export function useCreateExample() {
  const qc = useQueryClient();
  return useMutation<ExampleResponse, Error, UseCreateExampleVars>({
    mutationFn: ({ connId, body }) => createEnterpriseExample(connId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: enterpriseExamplesKey(vars.connId),
      });
    },
  });
}

export interface UseDeleteExampleVars {
  readonly connId: string;
  readonly exampleId: string;
}

export function useDeleteExample() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    UseDeleteExampleVars,
    { previous: readonly ExampleResponse[] | undefined }
  >({
    mutationFn: ({ connId, exampleId }) => deleteEnterpriseExample(connId, exampleId),
    onMutate: async (vars) => {
      const key = enterpriseExamplesKey(vars.connId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<readonly ExampleResponse[]>(key);
      qc.setQueryData<readonly ExampleResponse[]>(key, (old) =>
        (old ?? []).filter((e) => e.id !== vars.exampleId),
      );
      return { previous };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(enterpriseExamplesKey(vars.connId), ctx.previous);
      }
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: enterpriseExamplesKey(vars.connId) });
    },
  });
}
